import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from 'src/store/store';
import { UserMetricsDetail } from 'src/API';
import { fetchUserMetricsAPI } from 'src/services/api/userMetricsService';

interface UserMetricsState {
  userMetrics: UserMetricsDetail[];
  isLoading: boolean;
  error: string | null;
  hasAttemptedFetch: boolean;
}

const initialState: UserMetricsState = {
  userMetrics: [],
  isLoading: false,
  error: null,
  hasAttemptedFetch: false,
};

export const fetchUserMetrics = createAsyncThunk('userMetrics/fetchUserMetrics', async (_, { rejectWithValue }) => {
  try {
    return await fetchUserMetricsAPI();
  } catch (error) {
    return rejectWithValue((error as Error).message || 'Failed to fetch user metrics');
  }
});

const userMetricsSlice = createSlice({
  name: 'userMetrics',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    reset: () => initialState,
    resetFetchStatus: (state) => {
      state.hasAttemptedFetch = false;
    },
    resetLoadingStatus: (state) => {
      state.isLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchUserMetrics
      .addCase(fetchUserMetrics.pending, (state: UserMetricsState) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUserMetrics.fulfilled, (state: UserMetricsState, action) => {
        state.isLoading = false;
        state.hasAttemptedFetch = true;
        state.userMetrics = action.payload as UserMetricsDetail[];
      })
      .addCase(fetchUserMetrics.rejected, (state: UserMetricsState, action) => {
        state.isLoading = false;
        state.hasAttemptedFetch = true;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, resetFetchStatus, resetLoadingStatus, reset } = userMetricsSlice.actions;

export const selectUserMetrics = (state: RootState) => {
  const userMetricsState = state.userMetrics as UserMetricsState;
  return userMetricsState.userMetrics;
};

export const selectIsLoading = (state: RootState) => {
  const userMetricsState = state.userMetrics as UserMetricsState;
  return userMetricsState.isLoading;
};

export const selectError = (state: RootState) => {
  const userMetricsState = state.userMetrics as UserMetricsState;
  return userMetricsState.error;
};

export const selectHasAttemptedFetch = (state: RootState) => {
  const userMetricsState = state.userMetrics as UserMetricsState;
  return userMetricsState.hasAttemptedFetch;
};

export default userMetricsSlice.reducer;
